import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Truck, MapPin, Coffee, Clock, RefreshCw, Star, 
  Circle, ArrowRight, AlertTriangle, CheckCircle2,
  Navigation, Phone, User, Timer
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  off_duty: { label: 'Off Duty', color: 'bg-gray-100 text-gray-600', icon: Circle },
  on_route: { label: 'On Route', color: 'bg-green-100 text-green-700', icon: Truck },
  at_store: { label: 'At Store', color: 'bg-blue-100 text-blue-700', icon: MapPin },
  on_break: { label: 'On Break', color: 'bg-yellow-100 text-yellow-700', icon: Coffee },
  returning: { label: 'Returning', color: 'bg-purple-100 text-purple-700', icon: Navigation },
};

export default function DriverTrackingWidget() {
  const [data, setData] = useState({ drivers: [], summary: {}, last_updated: null });
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('drivers');

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      
      const [driversRes, activitiesRes] = await Promise.all([
        fetch(`${API_URL}/api/driver-tracking/drivers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/driver-tracking/activity-feed?limit=10`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (driversRes.ok) {
        const driversData = await driversRes.json();
        setData(driversData);
      }
      
      if (activitiesRes.ok) {
        const activitiesData = await activitiesRes.json();
        setActivities(activitiesData.activities || []);
      }
    } catch (error) {
      console.error('Failed to fetch driver tracking data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [fetchData]);

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = (now - date) / 1000 / 60; // minutes
    
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${Math.floor(diff)}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  };

  const { summary } = data;

  if (loading) {
    return (
      <Card className="h-full" data-testid="driver-tracking-widget">
        <CardContent className="flex items-center justify-center h-48">
          <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full overflow-hidden" data-testid="driver-tracking-widget">
      <CardHeader className="bg-gradient-to-r from-purple-600 to-purple-700 text-white py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            <CardTitle className="text-base font-bold">Driver Tracking</CardTitle>
          </div>
          <button onClick={fetchData} className="p-1 hover:bg-white/20 rounded">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </CardHeader>

      <CardContent className="p-3 space-y-3">
        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-2">
          <div className="text-center p-2 bg-green-50 rounded-lg">
            <p className="text-lg font-bold text-green-700">{summary.on_route || 0}</p>
            <p className="text-[10px] text-green-600">On Route</p>
          </div>
          <div className="text-center p-2 bg-blue-50 rounded-lg">
            <p className="text-lg font-bold text-blue-700">{summary.at_store || 0}</p>
            <p className="text-[10px] text-blue-600">At Store</p>
          </div>
          <div className="text-center p-2 bg-yellow-50 rounded-lg">
            <p className="text-lg font-bold text-yellow-700">{summary.on_break || 0}</p>
            <p className="text-[10px] text-yellow-600">On Break</p>
          </div>
          <div className="text-center p-2 bg-gray-50 rounded-lg">
            <p className="text-lg font-bold text-gray-700">{summary.off_duty || 0}</p>
            <p className="text-[10px] text-gray-600">Off Duty</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200">
          <button
            className={`flex-1 py-2 text-xs font-medium border-b-2 transition-colors ${
              activeTab === 'drivers' 
                ? 'border-purple-600 text-purple-600' 
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setActiveTab('drivers')}
          >
            Drivers ({data.drivers?.length || 0})
          </button>
          <button
            className={`flex-1 py-2 text-xs font-medium border-b-2 transition-colors ${
              activeTab === 'activity' 
                ? 'border-purple-600 text-purple-600' 
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setActiveTab('activity')}
          >
            Activity
          </button>
          <button
            className={`flex-1 py-2 text-xs font-medium border-b-2 transition-colors ${
              activeTab === 'feedback' 
                ? 'border-purple-600 text-purple-600' 
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setActiveTab('feedback')}
          >
            Feedback
          </button>
        </div>

        {/* Content */}
        <div className="max-h-48 overflow-y-auto">
          {activeTab === 'drivers' && (
            <div className="space-y-2">
              {data.drivers?.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No drivers found</p>
              ) : (
                data.drivers?.map(driver => {
                  const StatusIcon = STATUS_CONFIG[driver.current_status]?.icon || Circle;
                  // Calculate ETA display
                  const getEtaDisplay = () => {
                    if (!driver.eta_timestamp) return null;
                    const eta = new Date(driver.eta_timestamp);
                    const now = new Date();
                    const diffMs = eta - now;
                    if (diffMs <= 0) return 'Arriving now';
                    const diffMins = Math.ceil(diffMs / 60000);
                    if (diffMins < 60) return `${diffMins} min`;
                    const hours = Math.floor(diffMins / 60);
                    const mins = diffMins % 60;
                    return `${hours}h ${mins}m`;
                  };
                  const etaDisplay = getEtaDisplay();
                  
                  return (
                    <div 
                      key={driver.id}
                      className="flex items-center justify-between p-2 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                          <User className="h-4 w-4 text-purple-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{driver.name}</p>
                          <div className="flex items-center gap-1 text-[10px] text-slate-500">
                            <span className="truncate">
                              {driver.current_store || driver.current_location || 'No location'}
                            </span>
                            {driver.current_status === 'on_route' && etaDisplay && (
                              <span className="flex items-center gap-0.5 text-green-600 font-medium whitespace-nowrap">
                                <Timer className="h-3 w-3" />
                                {etaDisplay}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`text-[10px] ${STATUS_CONFIG[driver.current_status]?.color || 'bg-gray-100'}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {STATUS_CONFIG[driver.current_status]?.label || 'Unknown'}
                        </Badge>
                        {driver.latest_feedback?.rating && (
                          <div className="flex items-center text-yellow-500">
                            <Star className="h-3 w-3 fill-current" />
                            <span className="text-[10px] ml-0.5">{driver.latest_feedback.rating}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="space-y-2">
              {activities.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No recent activity</p>
              ) : (
                activities.map((activity, idx) => {
                  const StatusIcon = STATUS_CONFIG[activity.status]?.icon || Circle;
                  return (
                    <div 
                      key={activity.id || idx}
                      className="flex items-start gap-2 p-2 border-l-2 border-purple-200 bg-slate-50 rounded-r-lg"
                    >
                      <StatusIcon className={`h-4 w-4 mt-0.5 ${
                        activity.status === 'at_store' ? 'text-blue-500' :
                        activity.status === 'on_route' ? 'text-green-500' :
                        activity.status === 'on_break' ? 'text-yellow-500' : 'text-gray-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-800">
                          <span className="font-medium">{activity.driver_name}</span>
                          {' → '}
                          <span className={`font-medium ${
                            activity.status === 'at_store' ? 'text-blue-600' :
                            activity.status === 'on_route' ? 'text-green-600' :
                            activity.status === 'on_break' ? 'text-yellow-600' : 'text-gray-600'
                          }`}>
                            {activity.status_label || STATUS_CONFIG[activity.status]?.label}
                          </span>
                        </p>
                        {activity.store_name && (
                          <p className="text-[10px] text-slate-500 truncate">
                            <MapPin className="h-3 w-3 inline mr-0.5" />
                            {activity.store_name}
                          </p>
                        )}
                        <p className="text-[10px] text-slate-400">{formatTime(activity.timestamp)}</p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {activeTab === 'feedback' && (
            <div className="space-y-2">
              {data.drivers?.filter(d => d.latest_feedback).length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No recent feedback</p>
              ) : (
                data.drivers?.filter(d => d.latest_feedback).map(driver => (
                  <div 
                    key={driver.id}
                    className="p-2 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-slate-800">{driver.name}</span>
                      <div className="flex items-center">
                        {[1,2,3,4,5].map(star => (
                          <Star 
                            key={star} 
                            className={`h-3 w-3 ${
                              star <= (driver.latest_feedback?.rating || 0) 
                                ? 'text-yellow-400 fill-current' 
                                : 'text-gray-300'
                            }`} 
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-500">
                      From: {driver.latest_feedback?.store || 'Unknown store'}
                    </p>
                    {driver.latest_feedback?.feedback && (
                      <p className="text-[10px] text-slate-600 mt-1 italic">
                        "{driver.latest_feedback.feedback}"
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
